import cn from 'classnames';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import React from 'react';
import { DisparityOpportunityTitle } from 'apps/sigma2-ui/src/components/DisparityOpportunityTitle';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { DisparityOpportunityDescription } from 'apps/sigma2-ui/src/components/DisparityOpportunityDescription';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { DisparityBarChartStack } from 'apps/sigma2-ui/src/components/DisparityBarChartStack';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';

export interface TopOpportunityCardProps {
  metricDefinition: MyMetricDefinition;
  focusAreaDefinition: MyFocusAreaDefinition;
  referenceLevelDefinition: LevelDefinition;
  treatmentLevelDefinition: LevelDefinition;
  rawDisparityPercentage: number;
  controlledDisparityPercentage: number;
  debiasedDisparityPercentage: number | null;
  onCardClick: () => void;
  cardIsClickable: boolean;

  // Slot
  recommendedSolutionsCard?: React.ReactNode;
  calibrationFraming: CalibrationFraming;
}

export const TopOpportunityCard = ({
  metricDefinition,
  focusAreaDefinition,
  referenceLevelDefinition,
  treatmentLevelDefinition,
  recommendedSolutionsCard,
  onCardClick,
  rawDisparityPercentage,
  controlledDisparityPercentage,
  debiasedDisparityPercentage,
  cardIsClickable,
  calibrationFraming,
}: TopOpportunityCardProps) => {
  const mainCardClasses: string =
    'h-full' +
    (cardIsClickable
      ? ' shadow-0px hover:shadow-4px hover:-translate-y-1'
      : '');

  return (
    <Spacing
      size={'md'}
      direction={'vertical'}
      className="justify-between max-w-[33%] min-w-72 flex-1"
    >
      <Card paddingClassName={'p-0'} className={mainCardClasses}>
        <TouchTarget
          className="cursor-default"
          content={() => {
            return (
              <Spacing
                size={'sm'}
                direction={'vertical'}
                className={cn({ 'cursor-pointer': cardIsClickable })}
              >
                <Box
                  as={'div'}
                  pt={'sm'}
                  pb={'xs'}
                  px={'sm'}
                  justifyContent={'start'}
                  display={'flex'}
                  alignItems={'start'}
                >
                  <DisparityOpportunityTitle
                    treatmentGroup={treatmentLevelDefinition}
                    metricDefinition={metricDefinition}
                    typographyVariant={'headline-6-alt'}
                  />
                </Box>

                <Box as={'div'} w={'full'} minH="min-h-[1px]" bg="gray-200" />

                <Box
                  as={'div'}
                  p={'sm'}
                  justifyContent={'start'}
                  display={'flex'}
                  alignItems={'start'}
                >
                  <Spacing
                    size={'lg'}
                    direction={'vertical'}
                    className="text-left"
                  >
                    <Box display={'flex'} justifyContent={'center'}>
                      <DisparityBarChartStack
                        rawDisparity={rawDisparityPercentage}
                        controlledDisparity={controlledDisparityPercentage}
                        debiasedDisparity={debiasedDisparityPercentage}
                        variant={'default'}
                        calibrationFraming={calibrationFraming}
                        hasRunBiasTasks={metricDefinition.hasBiasTestTasks}
                      />
                    </Box>

                    <DisparityOpportunityDescription
                      treatmentGroup={treatmentLevelDefinition}
                      referenceGroup={referenceLevelDefinition}
                      metricDefinition={metricDefinition}
                      rawDisparityPercentage={rawDisparityPercentage}
                      controlledDisparityPercentage={
                        controlledDisparityPercentage
                      }
                      debiasedDisparityPercentage={debiasedDisparityPercentage}
                      controlVariableDisplayNames={[
                        ...metricDefinition.staticControlVariableDefinitions.map(
                          (variable) => variable.name,
                        ),
                        ...focusAreaDefinition.controlVariables.map(
                          (controlVariable) => controlVariable.displayName,
                        ),
                      ]}
                      calibrationFraming={calibrationFraming}
                      hasRunBiasTasks={metricDefinition.hasBiasTestTasks}
                    />
                  </Spacing>
                </Box>
              </Spacing>
            );
          }}
          behavior={{
            type: 'onClick',
            onClick: () => {
              if (cardIsClickable) {
                onCardClick?.();
              }
            },
          }}
        />
      </Card>
      {recommendedSolutionsCard}
    </Spacing>
  );
};
